import type { PropsWithChildren, ReactElement } from 'react';
import styles from './PackageDetailsPackageSpecs.module.scss';
import FontAwesomeIcon, {
  type Icon,
  lightClock,
  lightCalendarDays,
  lightUserGroup,
  lightCoins
} from 'common/components/FontAwesomeIcon';
import { summarizeCapacity } from 'packages/show/components/common/helpers/summarizeCapacity';
import { summarizeDuration } from 'packages/show/components/common/helpers/summarizeDuration';
import type { Package } from 'packages/types';
import { pricesToText, vatText } from 'packages/utils';

type Props = {
  availability: Package['availability'];
  duration: Package['duration'];
  capacity: Package['capacity'];
  pricing: Package['pricing'];
};

export function PackageDetailsPackageSpecs({
  availability,
  duration,
  capacity,
  pricing
}: Props): ReactElement {
  return (
    <ul className={`fa-ul ${styles.container}`}>
      {availability ? (
        <AttributeRow
          icon={lightCalendarDays}
          label={__('packages.details.availability')}
        >
          {availability}
        </AttributeRow>
      ) : null}
      {typeof duration.min === 'number' || typeof duration.max === 'number' ? (
        <AttributeRow
          icon={lightClock}
          label={__('packages.details.duration.label')}
        >
          {summarizeDuration(duration)}
        </AttributeRow>
      ) : null}
      {typeof capacity.min === 'number' || typeof capacity.max === 'number' ? (
        <AttributeRow
          icon={lightUserGroup}
          label={__('packages.details.capacity.label')}
        >
          {summarizeCapacity(capacity)}
        </AttributeRow>
      ) : null}
      <AttributeRow icon={lightCoins} label={__('packages.details.price')}>
        {pricesToText(pricing)}{' '}
        <span className={styles.pricingInfo}>
          ({vatText(pricing.vatIncluded)})
        </span>
      </AttributeRow>
    </ul>
  );
}

type AttributeRowProps = PropsWithChildren<{
  icon: Icon;
  label: string;
}>;

function AttributeRow({
  icon,
  label,
  children
}: AttributeRowProps): ReactElement {
  return (
    <li className={styles.row}>
      <span className="fa-li">
        <FontAwesomeIcon icon={icon} modifier="fw" />
      </span>
      <span className={styles.label}>
        {label}
        {': '}
      </span>
      {children}
    </li>
  );
}
